import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import Form from 'dashboard/layouts/FormLayout/Form';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea/TextArea';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FormGraphQueryTemplateSaveDataV2 {
  name: string;
  description: string;
  id: string;
}

interface FormGraphQueryTemplateSaveV2Props {
  graphQueryTemplate: GraphQueryTemplate | null;
  graphQueryTemplates: GraphQueryTemplate[];
  onSubmit: (data: FormGraphQueryTemplateSaveDataV2) => void;
  onCancel: () => void;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled(Button)`
  width: 75px;
  display: flex;
  margin-top: ${Spacing[4]};
  margin-right: ${Spacing[4]};
`;

const ConfirmButton = styled(Button)`
  width: 75px;
  display: flex;
  margin-top: ${Spacing[4]};
`;

const FormGraphQueryTemplateSaveV2 = ({
  graphQueryTemplate,
  graphQueryTemplates = [],
  onSubmit,
  onCancel,
}: FormGraphQueryTemplateSaveV2Props) => {
  const { control, handleSubmit } = useFormSecurely();
  // create toggle for confirmation modal using useToggle hook
  const [
    isConfirmationModalOpen,
    { enable: openConfirmationModal, disable: closeConfirmationModal },
  ] = useToggle(false);

  const [formData, setFormData] = useState<FormGraphQueryTemplateSaveDataV2 | null>(null);
  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'name',
    defaultValue: graphQueryTemplate
      ? {
          label: graphQueryTemplate.attributes.name,
          value: graphQueryTemplate.id,
        }
      : null,
  });
  const {
    field: { onChange: onDescriptionChange, value: description },
  } = useController({
    control,
    name: 'description',
    defaultValue: graphQueryTemplate ? graphQueryTemplate.attributes.description : null,
  });

  const options = graphQueryTemplates
    .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name))
    .map((gqt: GraphQueryTemplate) => ({
      label: gqt.attributes.name,
      value: gqt.id,
    }));

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        const newFormData = {
          name: data.name.label,
          id: data.name.value,
          description: data.description,
        };

        if (newFormData.id == null) {
          onSubmit(newFormData);
          return;
        }
        setFormData(newFormData);
        openConfirmationModal();
      })}
    >
      <Label size='lg' weight='bold'>
        Template name
      </Label>
      <ReactCreatableSelect
        name='name'
        placeholder={null}
        value={value}
        options={options}
        preserveOptionOnChange={true}
        onChange={(event) => {
          if (!event) {
            onChange(null);
            onDescriptionChange('');
            return;
          }

          const isNewEvent = event.__isNew__;
          onChange({
            ...event,
            value: isNewEvent ? null : event.value,
          });
          if (!isNewEvent) {
            onDescriptionChange(
              graphQueryTemplates.find((gqt) => gqt.id === event.value)?.attributes.description,
            );
          } else {
            onDescriptionChange('');
          }
        }}
        portal={true}
      />
      <Spacer height={3} />
      <Label size='lg' weight='bold'>
        Description (optional)
      </Label>
      <TextArea
        id='description'
        name='description'
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
      />
      <ButtonContainer>
        <CancelButton variant='basic' onClick={onCancel}>
          Cancel
        </CancelButton>
        <ConfirmButton type='submit'>Confirm</ConfirmButton>
      </ButtonContainer>
      <ModalWrapper isOpen={isConfirmationModalOpen} handleCloseModal={closeConfirmationModal}>
        <ConfirmationModal
          header='Would you like to overwrite your existing graph query template?'
          cancelAction={closeConfirmationModal}
          confirmAction={() => {
            formData && onSubmit(formData);
          }}
          cancelActionText='Cancel'
          confirmActionText='Overwrite'
        >
          This action will overwrite your existing template.
        </ConfirmationModal>
      </ModalWrapper>
    </Form>
  );
};

export default FormGraphQueryTemplateSaveV2;
